import { PARTIAL } from '../types/SearchTypes';
import { HighlightedText } from '../components/shared/HighlightedText';
import { jsx as _jsx } from "react/jsx-runtime";
const UTTERANCE_DISTANCE = 10;
const PunctuationRegex = /[-.,\\/#!+?$%^&*;:{}=_`~()]/g; // matches all punctuation characters, equivalent to "\\p{Punct}"
const EscapeReplacementRegex = '\\$&'; // escapes special characters
const SearchRegexParams = 'gi'; // global, case insensitive

// As per https://bugs.webkit.org/show_bug.cgi?id=174931 this has now been fixed
// eslint-disable-next-line lookbehind-assertions/no-lookbehind-assertions-regexp
const LookBehindRegex = '(?<![a-zA-Z])';
const LookAheadRegex = '(?![a-zA-Z])';
const BlockRegex = '\\b';
export function getRegEx(searchTerm, searchType) {
  const escapedSearchTerm = searchTerm.replace(PunctuationRegex, EscapeReplacementRegex);
  if (searchType === PARTIAL) {
    return new RegExp(escapedSearchTerm, SearchRegexParams);
  }
  // This util will mimic the behaviour of the ci-be: https://git.hubteam.com/HubSpot/ci-be/blob/6c7fddb64b9cbaab99752604b473e3be4f0d1618/ci-be-data/src/main/java/com/hubspot/ci/be/data/helpers/tracked/terms/TrackedTermsCountFinder.java
  if (searchTerm.match(PunctuationRegex)) {
    if (searchTerm.length === 1) {
      return new RegExp(escapedSearchTerm, SearchRegexParams);
    }
    return new RegExp(`${LookBehindRegex}${escapedSearchTerm}${LookAheadRegex}`, SearchRegexParams);
  }
  return new RegExp(`${BlockRegex}${escapedSearchTerm}${BlockRegex}`, SearchRegexParams);
}
export function highlightMatchingTermInInsightsUtterance(text, searchTerm, searchType, HighlightStyleComponent) {
  const regExp = getRegEx(searchTerm, searchType);
  const matches = text.match(regExp);
  if (!matches) return text;
  const parts = text.split(regExp);
  return parts.reduce((arr, element, index) => {
    if (matches[index]) {
      arr.push(element, /*#__PURE__*/_jsx(HighlightStyleComponent, {
        children: matches[index]
      }, `${index}-${element}`));
    } else {
      arr.push(element);
    }
    return arr;
  }, []);
}
const filterByDistance = resultIndexPerUtterance => {
  const filterResults = [...resultIndexPerUtterance];
  const index = resultIndexPerUtterance.findIndex(a => a !== null && a !== undefined);
  const bottom = Math.max(index - UTTERANCE_DISTANCE, 0);
  for (let i = bottom; i < index; i++) {
    filterResults[i] = null;
  }
  const top = Math.min(index + UTTERANCE_DISTANCE, resultIndexPerUtterance.length);
  for (let i = index + top; i < resultIndexPerUtterance.length; i++) {
    filterResults[i] = null;
  }
  return filterResults;
};
function numMatchesInText(text, searchTerm, searchType) {
  if (!searchTerm) return 0;
  const regExp = getRegEx(searchTerm, searchType);
  const matches = text.match(regExp);
  return matches ? matches.length : 0;
}
export function highlightMatchingTermInTranscriptUtterance(text, searchTerm, searchType, resultIndexInUtterance) {
  const regExp = getRegEx(searchTerm, searchType);
  const matches = text.match(regExp);
  if (!matches) return text;
  const parts = text.split(regExp);
  let isFirstOccurrenceHighlighted = resultIndexInUtterance === undefined;
  return parts.reduce((arr, element, index) => {
    if (matches[index]) {
      if (!isFirstOccurrenceHighlighted && index === resultIndexInUtterance) {
        arr.push(element, /*#__PURE__*/_jsx(HighlightedText, {
          "data-test-id": "styledTranscriptSearchMatch",
          children: matches[index]
        }));
        isFirstOccurrenceHighlighted = true;
      } else {
        arr.push(element, /*#__PURE__*/_jsx(HighlightedText, {
          "data-test-id": "boldTranscriptSearchMatch",
          children: matches[index]
        }));
      }
    } else {
      arr.push(element);
    }
    return arr;
  }, []);
}
export function getSearchResultsAndTranscript(transcript, searchTerm, searchResultIndex, searchType) {
  let resultIndexPerUtterance = [];
  let matchedUtterance = null;
  let totalMatches = 0;
  let found = false;

  // Loop over the transcript to identify the index of first occurance of search term and highlight terms appropriately
  const highlightedOrFullTranscript = transcript.map((utteranceData, index) => {
    const numMatchesInUtterance = numMatchesInText(utteranceData.utterance, searchTerm, searchType);
    let parsedUtterance;
    const prevTotal = totalMatches;
    totalMatches += numMatchesInUtterance;

    /* This condition is to determine which utterance contains the searchResultIndex - th match
      and where in that utterance is the match
     */
    if (!found && prevTotal - 1 <= searchResultIndex && searchResultIndex <= totalMatches - 1) {
      const resultIndexInUtterance = searchResultIndex - prevTotal;
      resultIndexPerUtterance[index] = resultIndexInUtterance;
      matchedUtterance = utteranceData;
      found = true;

      // when we find the match index we highlight it with a box around it and rest with bold
      parsedUtterance = highlightMatchingTermInTranscriptUtterance(utteranceData.utterance, searchTerm, searchType, resultIndexInUtterance);
    } else {
      // other matches are just bold
      parsedUtterance = highlightMatchingTermInTranscriptUtterance(utteranceData.utterance, searchTerm, searchType);
      resultIndexPerUtterance[index] = null;
    }
    return Object.assign({}, utteranceData, {
      utterance: parsedUtterance
    });
  });
  if (found) {
    resultIndexPerUtterance = filterByDistance(resultIndexPerUtterance);
  }
  return {
    resultIndexPerUtterance,
    matchedUtterance,
    totalMatches,
    highlightedOrFullTranscript
  };
}